import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Skeleton } from 'ui/skeleton';
import { HelpToggletip } from 'ui/tooltip';
import {
  metricSentenceCase,
  getChartActions,
  getChartExportingOptions,
  formatNumberGivenValueType,
} from 'modules/productAnalytics/utils';
import { StyledDivider } from 'modules/productAnalytics/Styled';
import { selectIsInAppRecallUIFeatureFlagOn } from 'core/userContext';

import { EngagementConversionColumnChart } from './EngagementConversionColumnChart';
import {
  SkeletonWrapper,
  OverviewCardContainer,
  MetricBreakdownSummaryContainer,
  MetricBreakdownMainMetric,
  MetricBreakdownSubMetricWrappers,
  MetricBreakdownMetricTitle,
  MetricBreakdownSubMetricValue,
  MetricBreakdownToggletipContainer,
  GraphContainer,
  MetricBreakdownHeaderValue,
} from './Styled';
import { MetricBreakdownCard } from './MetricBreakdownCard';

import {
  ENGAGEMENT_MULTI_CONVERSION_DEFAULT,
  ENGAGEMENT_CONVERSION_GRAPH_WITH_CONVERSIONS,
} from '../constants';
import { parseMetricsForTimeSeriesGraph } from '../utils';
import {
  selectCampaignAnalyticsStatsForTimerange,
  selectMessageMedium,
  selectIsLoading,
  selectAnalyticsMetricsValueTypes,
} from '../selectors';
import { EngagementAreaChart } from './EngagementAreaChart';
import { MetricDefinitionWrapper } from './MetricDefinitionWrapper';

/**
 * Helper method which will find the
 * values for a set of given metrics
 */
const metricsResultFinder = (metricList, results) =>
  metricList.reduce((acc, { key, ...metric }) => {
    if (typeof results?.[key]?.value !== 'undefined') {
      return [
        ...acc,
        {
          key,
          value: results[key]?.value,
          ...metric,
          label: metricSentenceCase(metric?.label),
        },
      ];
    }
    return acc;
  }, []);

const engagementOverviewByType = (messageMedium, results = []) => {
  if (Object.keys(results).length === 0) {
    return [];
  }
  const engagementOverviewMetrics = ENGAGEMENT_MULTI_CONVERSION_DEFAULT[messageMedium] || [];
  return metricsResultFinder(engagementOverviewMetrics, results);
};

const engagementGraphByType = (messageMedium, results) => {
  const inAppRecallFF = useSelector(selectIsInAppRecallUIFeatureFlagOn);

  const engagementGraphMetrics =
    ENGAGEMENT_CONVERSION_GRAPH_WITH_CONVERSIONS(inAppRecallFF)[messageMedium] || [];
  const engagementMetricsWithValue = metricsResultFinder(engagementGraphMetrics, results);

  // Setting a display name for our chart components for testing purpose
  EngagementAreaChart.displayName = 'EngagementAreaChart';
  EngagementConversionColumnChart.displayName = 'EngagementConversionColumnChart';

  // In case of SMS message medium. There could be a scenario of no conversion value.
  // In which case we would just have one metric to plot against. In which case we
  // will be using an area graph
  return engagementMetricsWithValue.length === 1
    ? {
        Graph: EngagementAreaChart,
        engagementGraphData: parseMetricsForTimeSeriesGraph(
          engagementGraphMetrics.map(({ key }) => key),
          results,
        ),
      }
    : {
        Graph: EngagementConversionColumnChart,
        engagementGraphData: engagementMetricsWithValue,
      };
};

const getOverviewAndGraphMetrics = (messageMedium, statsForTimeRange = {}) => {
  const { results = {} } = statsForTimeRange;
  return {
    engagementOverview: engagementOverviewByType(messageMedium, results),
    ...engagementGraphByType(messageMedium, results),
  };
};

const MetricBreakdownSummary = ({ engagementOverview }) => {
  const [mainMetric, ...subMetrics] = engagementOverview;
  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);

  return (
    <MetricBreakdownSummaryContainer>
      <MetricBreakdownMainMetric data-testid="engagement-main-metric">
        <MetricBreakdownMetricTitle data-testid="engagement-main-metric-title">
          <MetricDefinitionWrapper metricName={mainMetric?.label}>
            {metricSentenceCase(mainMetric?.label)}
          </MetricDefinitionWrapper>
          {mainMetric?.tooltip && (
            <MetricBreakdownToggletipContainer>
              <HelpToggletip
                tooltipContent={mainMetric.tooltip}
                iconProps={{ width: 18, height: 18 }}
              />
            </MetricBreakdownToggletipContainer>
          )}
        </MetricBreakdownMetricTitle>
        <MetricBreakdownHeaderValue data-testid="engagement-main-metric-value">
          {formatNumberGivenValueType(valueTypes[mainMetric?.key], mainMetric?.value)}
        </MetricBreakdownHeaderValue>
      </MetricBreakdownMainMetric>
      {subMetrics.map((metric, index) => {
        const MetricBreakdownSubMetric = MetricBreakdownSubMetricWrappers[index];
        return (
          <MetricBreakdownSubMetric key={metric?.key} data-testid="engagement-sub-metric">
            <MetricBreakdownMetricTitle data-testid="engagement-sub-metric-title">
              <MetricDefinitionWrapper metricName={metric?.label}>
                {metricSentenceCase(metric?.label)}
              </MetricDefinitionWrapper>
            </MetricBreakdownMetricTitle>
            <MetricBreakdownSubMetricValue data-testid="engagement-sub-metric-value">
              {formatNumberGivenValueType(valueTypes[metric?.key], metric?.value)}
            </MetricBreakdownSubMetricValue>
          </MetricBreakdownSubMetric>
        );
      })}
    </MetricBreakdownSummaryContainer>
  );
};

export const EngagementOverview = () => {
  const { search } = useLocation();
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const messageMedium = useSelector(selectMessageMedium);
  const isLoading = useSelector(selectIsLoading);
  const chartRef = useRef();
  const chartId = 'engagementOverviewChart';
  const chartActions = getChartActions(chartId, chartRef);

  const { Graph, engagementOverview, engagementGraphData } = getOverviewAndGraphMetrics(
    messageMedium,
    statsForTimeRange,
  );

  return (
    <MetricBreakdownCard
      title={'Engagement'}
      linkout={{
        href: '/analytics/campaignPerformance/engagement',
        search,
      }}
      data-test="engagement-overview"
      menuOptions={getChartExportingOptions(chartId)}
      handleSelectionChange={(key) => chartActions[key]()}
    >
      {isLoading ? (
        <SkeletonWrapper height={576} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      ) : (
        <OverviewCardContainer>
          <MetricBreakdownSummary engagementOverview={engagementOverview} />
          <StyledDivider />
          <GraphContainer data-testid={`engagement-graph-${Graph.displayName}`}>
            <Graph data={engagementGraphData} ref={chartRef} />
          </GraphContainer>
        </OverviewCardContainer>
      )}
    </MetricBreakdownCard>
  );
};
